import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'
import { supportedLanguages, defaultLanguage, type SupportedLanguage } from '@/i18n'

// Nav items — add/remove entries here and make sure each key exists in
// `src/i18n/routes.ts` and has a `nav.*` translation in the locales files.
const navItems: { key: RouteKey; labelKey: string }[] = [
  { key: 'home', labelKey: 'nav.home' },
  { key: 'about', labelKey: 'nav.about' },
  { key: 'services', labelKey: 'nav.services' },
  { key: 'machines', labelKey: 'nav.machines' },
  { key: 'news', labelKey: 'nav.news' },
  { key: 'events', labelKey: 'nav.events' },
  { key: 'partners', labelKey: 'nav.partners' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

export function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)

  // Current language (first segment) and slug (second segment)
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const rawLang = pathSegments[0] as SupportedLanguage | undefined
  const currentLang: SupportedLanguage =
    rawLang && (supportedLanguages as readonly string[]).includes(rawLang)
      ? rawLang
      : defaultLanguage
  const currentSlug = pathSegments[1] || ''

  const isRouteActive = (key: RouteKey) => {
    if (key === 'home') return currentSlug === ''
    return localizedRoutes[key][currentLang] === currentSlug
  }

  // Detect scroll
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false)
  }, [location.pathname])

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileOpen])

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-300',
        'bg-white',
        isScrolled ? 'shadow-[0_2px_16px_-4px_rgba(18,52,82,0.12)]' : 'shadow-none'
      )}
    >
      {/* Top accent bar — industrial brand signature */}
      <div
        aria-hidden="true"
        className="h-1 w-full bg-gradient-to-r from-brand-500 via-brand-600 to-brand-500"
      />

      <nav
        aria-label={t('aria.mainNavigation', 'Main navigation')}
        className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-16 md:h-20"
      >
        {/* Logo */}
        <LocalizedLink to="home" className="flex-shrink-0 flex items-center" aria-label={t('aria.homeLink')}>
          <img
            src="/images/logo.png"
            alt={t('aria.logoAlt')}
            className="h-9 md:h-11 w-auto select-none"
            draggable={false}
          />
        </LocalizedLink>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-0.5">
          {navItems.map(({ key, labelKey }) => {
            const isActive = isRouteActive(key)

            return (
              <LocalizedLink
                key={key}
                to={key}
                className={cn(
                  'group relative px-3 py-2 text-[13px] font-semibold uppercase tracking-[0.08em] rounded-sm transition-colors',
                  isActive
                    ? 'text-brand-600'
                    : 'text-ink-700 hover:text-brand-600'
                )}
              >
                {t(labelKey)}
                {/* Active indicator — brand-blue underline */}
                <span
                  className={cn(
                    'absolute bottom-0 left-3 right-3 h-[2px] bg-brand-500 origin-center transition-transform duration-200 ease-out',
                    isActive
                      ? 'scale-x-100'
                      : 'scale-x-0 group-hover:scale-x-100'
                  )}
                />
              </LocalizedLink>
            )
          })}
        </div>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center gap-2 pl-3 ml-1 border-l border-ink-200">
          <LanguageSelector variant="desktop" isScrolled={true} />
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className={cn(
            'lg:hidden p-2 rounded-sm transition-colors',
            'text-ink-800 hover:bg-ink-100'
          )}
          aria-label={isMobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
          aria-expanded={isMobileOpen}
        >
          {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="lg:hidden fixed inset-0 top-[calc(4rem+4px)] md:top-[calc(5rem+4px)] bg-white z-40 overflow-y-auto"
          >
            <nav
              className="flex flex-col py-2"
              aria-label={t('aria.mobileNavigation', 'Mobile navigation')}
            >
              {navItems.map(({ key, labelKey }, idx) => {
                const isActive = isRouteActive(key)

                return (
                  <LocalizedLink
                    key={key}
                    to={key}
                    className={cn(
                      'px-6 py-4 text-base font-semibold uppercase tracking-[0.08em] border-b border-ink-100 transition-colors',
                      isActive
                        ? 'text-brand-600 bg-brand-50 border-l-4 border-l-brand-500 pl-5'
                        : 'text-ink-700 hover:bg-ink-100'
                    )}
                    style={{ animationDelay: `${idx * 30}ms` }}
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                )
              })}
              <div className="mt-4 px-6 py-4">
                <LanguageSelector variant="mobile" />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
